import * as THREE from 'three';
import { buildCircleFromThreePoints2D } from '../../tool/buildCircleFromThreePoints2D';
import { isFeatureCurve } from '../../mesh/feature/FeatureCurve';
import { isFeatureLine } from '../../mesh/feature/FeatureLine';
import type { FeatureSurface } from '../../mesh/feature/FeatureSurface';
import { AxisSurfaceType, ReferenceEntitySemanticType } from '../ReferenceFeatureTypes';

/** 圆心拟合允许的最小点数。 */
const MIN_CIRCLE_POINT_COUNT = 3;

/** 几何退化容差。 */
const GEOMETRY_EPSILON = 1e-8;

/** 轴方向退化容差。 */
const AXIS_EPSILON = 1e-12;

/** 曲面法向回退采样上限。 */
const SURFACE_SAMPLE_LIMIT = 48;

/**
 * 基准轴可消费的曲面轴解析结果。
 */
export interface FeatureSurfaceAxisResolution {
  /** 解析后的实体类型。 */
  entityType: ReferenceEntitySemanticType.FeatureSurface;
  /** 解析后的解析曲面类型。 */
  surfaceType: AxisSurfaceType;
  /** 轴线上一点。 */
  axisOrigin: THREE.Vector3;
  /** 轴方向。 */
  axisDirection: THREE.Vector3;
  /** 轴显示长度。 */
  axisDisplayLength?: number;
  /** 曲面代表点。 */
  surfacePoint: THREE.Vector3;
  /** 外环顶点。 */
  faceOuterLoopVertices: THREE.Vector3[];
}

/**
 * 圆边界解析结果。
 */
interface CircularBoundaryGeometry {
  /** 圆心。 */
  center: THREE.Vector3;
  /** 圆面法向。 */
  normal: THREE.Vector3;
}

/**
 * 轴语义映射结果。
 */
interface SupportedSurfaceAxisKind {
  /** 实体类型。 */
  entityType: ReferenceEntitySemanticType.FeatureSurface;
  /** 曲面类型。 */
  surfaceType: AxisSurfaceType;
}

/**
 * 为法向构造正交基。
 *
 * @param normal 法向方向。
 * @returns 二维局部基。
 */
function buildStablePlaneBasis(normal: THREE.Vector3): { u: THREE.Vector3; v: THREE.Vector3 } {
  const seed = Math.abs(normal.x) < 0.9
    ? new THREE.Vector3(1, 0, 0)
    : new THREE.Vector3(0, 1, 0);
  const u = seed.clone().sub(normal.clone().multiplyScalar(seed.dot(normal)));
  if (u.lengthSq() <= AXIS_EPSILON) {
    u.set(0, 0, 1).sub(normal.clone().multiplyScalar(normal.z));
  }
  u.normalize();
  const v = normal.clone().cross(u).normalize();
  return { u, v };
}

/**
 * 去除曲线采样中的重复点。
 *
 * @param points 原始点集。
 * @returns 去重后的点集。
 */
function dedupeSamplePoints(points: THREE.Vector3[]): THREE.Vector3[] {
  const deduped: THREE.Vector3[] = [];
  points.forEach((point) => {
    const last = deduped[deduped.length - 1] ?? null;
    if (!last || last.distanceToSquared(point) > GEOMETRY_EPSILON) {
      deduped.push(point.clone());
    }
  });

  if (deduped.length >= 2 && deduped[0].distanceToSquared(deduped[deduped.length - 1]) <= GEOMETRY_EPSILON) {
    deduped.pop();
  }
  return deduped;
}

/**
 * 构建稳定的采样索引锚点。
 *
 * @param count 点数量。
 * @returns 锚点索引。
 */
function buildAnchorIndices(count: number): number[] {
  const anchors = new Set<number>([
    0,
    Math.floor(count / 4),
    Math.floor(count / 2),
    Math.floor((count * 3) / 4),
    count - 1,
  ]);
  return Array.from(anchors)
    .filter((index) => index >= 0 && index < count)
    .sort((left, right) => left - right);
}

/**
 * 生成锚点三元组。
 *
 * @param count 点数量。
 * @returns 三元组索引列表。
 */
function buildAnchorTriples(count: number): Array<[number, number, number]> {
  const anchors = buildAnchorIndices(count);
  const triples: Array<[number, number, number]> = [];
  for (let firstIndex = 0; firstIndex < anchors.length; firstIndex += 1) {
    for (let secondIndex = firstIndex + 1; secondIndex < anchors.length; secondIndex += 1) {
      for (let thirdIndex = secondIndex + 1; thirdIndex < anchors.length; thirdIndex += 1) {
        triples.push([
          anchors[firstIndex],
          anchors[secondIndex],
          anchors[thirdIndex],
        ]);
      }
    }
  }
  return triples;
}

/**
 * 读取特征曲线的世界坐标采样点。
 *
 * @param curve 特征曲线。
 * @returns 世界坐标采样点。
 */
function readFeatureCurveWorldSamplePoints(curve: THREE.Object3D): THREE.Vector3[] {
  if (isFeatureLine(curve)) {
    return dedupeSamplePoints(curve.getWorldSamplePoints());
  }

  const geometry = Reflect.get(curve, 'geometry');
  if (!(geometry instanceof THREE.BufferGeometry)) {
    return [];
  }
  const positionAttribute = geometry.getAttribute('position');
  if (!(positionAttribute instanceof THREE.BufferAttribute) || positionAttribute.count < MIN_CIRCLE_POINT_COUNT) {
    return [];
  }

  curve.updateWorldMatrix(true, false);
  const matrixWorld = curve.matrixWorld.clone();
  const points: THREE.Vector3[] = [];
  for (let index = 0; index < positionAttribute.count; index += 1) {
    points.push(
      new THREE.Vector3(
        positionAttribute.getX(index),
        positionAttribute.getY(index),
        positionAttribute.getZ(index),
      ).applyMatrix4(matrixWorld),
    );
  }
  return dedupeSamplePoints(points);
}

/**
 * 根据三维圆弧/圆采样点解析圆心与法向。
 *
 * @param points 世界坐标采样点。
 * @returns 解析结果；无法稳定解析时返回 `null`。
 */
function resolveCircularBoundaryGeometry(points: THREE.Vector3[]): CircularBoundaryGeometry | null {
  if (points.length < MIN_CIRCLE_POINT_COUNT) {
    return null;
  }

  const triples = buildAnchorTriples(points.length);
  for (const [firstIndex, secondIndex, thirdIndex] of triples) {
    const first = points[firstIndex];
    const second = points[secondIndex];
    const third = points[thirdIndex];
    const tangentA = second.clone().sub(first);
    const tangentB = third.clone().sub(first);
    const normal = tangentA.clone().cross(tangentB);
    if (normal.lengthSq() <= AXIS_EPSILON) {
      continue;
    }

    normal.normalize();
    const { u, v } = buildStablePlaneBasis(normal);
    const circle = buildCircleFromThreePoints2D(
      new THREE.Vector2(0, 0),
      new THREE.Vector2(
        second.clone().sub(first).dot(u),
        second.clone().sub(first).dot(v),
      ),
      new THREE.Vector2(
        third.clone().sub(first).dot(u),
        third.clone().sub(first).dot(v),
      ),
    );
    if (!circle || !Number.isFinite(circle.radius)) {
      continue;
    }

    const center = first.clone()
      .add(u.clone().multiplyScalar(circle.center.x))
      .add(v.clone().multiplyScalar(circle.center.y));
    return {
      center,
      normal,
    };
  }

  return null;
}

/**
 * 读取曲面边界中的圆形边。
 *
 * @param surface 曲面对象。
 * @returns 圆边界集合。
 */
function readCircularBoundaries(surface: FeatureSurface): CircularBoundaryGeometry[] {
  return surface.getBoundaryLines()
    .filter((line) => isFeatureCurve(line) && line.curveKind === 'circle')
    .map((line) => resolveCircularBoundaryGeometry(readFeatureCurveWorldSamplePoints(line)))
    .filter((circle): circle is CircularBoundaryGeometry => circle !== null);
}

/**
 * 从曲面几何中读取有限的世界坐标点与法向。
 *
 * @param surface 曲面对象。
 * @returns 采样结果。
 */
function readSurfaceWorldSamples(surface: FeatureSurface): {
  points: THREE.Vector3[];
  normals: THREE.Vector3[];
} {
  const positionAttribute = surface.geometry.getAttribute('position');
  const normalAttribute = surface.geometry.getAttribute('normal');
  if (!(positionAttribute instanceof THREE.BufferAttribute) || !(normalAttribute instanceof THREE.BufferAttribute)) {
    return { points: [], normals: [] };
  }

  surface.updateWorldMatrix(true, false);
  const normalMatrix = new THREE.Matrix3().getNormalMatrix(surface.matrixWorld);
  const indexAttribute = surface.geometry.getIndex();
  const rawIndices: number[] = [];
  if (indexAttribute instanceof THREE.BufferAttribute) {
    const step = Math.max(1, Math.floor(indexAttribute.count / SURFACE_SAMPLE_LIMIT));
    for (let cursor = 0; cursor < indexAttribute.count; cursor += step) {
      rawIndices.push(Number(indexAttribute.getX(cursor)));
    }
  } else {
    const step = Math.max(1, Math.floor(positionAttribute.count / SURFACE_SAMPLE_LIMIT));
    for (let cursor = 0; cursor < positionAttribute.count; cursor += step) {
      rawIndices.push(cursor);
    }
  }

  const seen = new Set<number>();
  const points: THREE.Vector3[] = [];
  const normals: THREE.Vector3[] = [];
  rawIndices.forEach((vertexIndex) => {
    if (seen.has(vertexIndex) || vertexIndex < 0 || vertexIndex >= positionAttribute.count) {
      return;
    }
    seen.add(vertexIndex);
    const point = new THREE.Vector3(
      positionAttribute.getX(vertexIndex),
      positionAttribute.getY(vertexIndex),
      positionAttribute.getZ(vertexIndex),
    ).applyMatrix4(surface.matrixWorld);
    const normal = new THREE.Vector3(
      normalAttribute.getX(vertexIndex),
      normalAttribute.getY(vertexIndex),
      normalAttribute.getZ(vertexIndex),
    ).applyMatrix3(normalMatrix);
    if (normal.lengthSq() <= AXIS_EPSILON) {
      return;
    }
    points.push(point);
    normals.push(normal.normalize());
  });

  return { points, normals };
}

/**
 * 从法向集合拟合圆柱轴方向。
 *
 * @param normals 世界坐标法向集合。
 * @returns 轴方向；无法拟合时返回 `null`。
 */
function resolveCylinderAxisDirectionFromNormals(normals: THREE.Vector3[]): THREE.Vector3 | null {
  const accumulated = new THREE.Vector3();
  for (let firstIndex = 0; firstIndex < normals.length; firstIndex += 1) {
    for (let secondIndex = firstIndex + 1; secondIndex < normals.length; secondIndex += 1) {
      const cross = normals[firstIndex].clone().cross(normals[secondIndex]);
      if (cross.lengthSq() <= GEOMETRY_EPSILON) {
        continue;
      }
      cross.normalize();
      if (accumulated.lengthSq() > AXIS_EPSILON && accumulated.dot(cross) < 0) {
        cross.multiplyScalar(-1);
      }
      accumulated.add(cross);
    }
  }

  if (accumulated.lengthSq() <= AXIS_EPSILON) {
    return null;
  }
  return accumulated.normalize();
}

/**
 * 在给定轴方向下，从曲面采样点投影拟合轴上一点。
 *
 * @param points 曲面采样点。
 * @param axisDirection 轴方向。
 * @returns 轴上一点；无法拟合时返回 `null`。
 */
function resolveAxisOriginFromProjectedCircle(
  points: THREE.Vector3[],
  axisDirection: THREE.Vector3,
): THREE.Vector3 | null {
  if (points.length < MIN_CIRCLE_POINT_COUNT) {
    return null;
  }

  const axis = axisDirection.clone().normalize();
  const { u, v } = buildStablePlaneBasis(axis);
  const projected = points.map((point) => new THREE.Vector2(point.dot(u), point.dot(v)));
  const triples = buildAnchorTriples(projected.length);
  for (const [firstIndex, secondIndex, thirdIndex] of triples) {
    const circle = buildCircleFromThreePoints2D(
      projected[firstIndex],
      projected[secondIndex],
      projected[thirdIndex],
    );
    if (!circle || !Number.isFinite(circle.radius)) {
      continue;
    }

    const averageAxisOffset = points.reduce((sum, point) => sum + point.dot(axis), 0) / points.length;
    return u.clone().multiplyScalar(circle.center.x)
      .add(v.clone().multiplyScalar(circle.center.y))
      .add(axis.clone().multiplyScalar(averageAxisOffset));
  }

  return null;
}

/**
 * 计算点集沿指定轴方向的投影跨度。
 *
 * @param points 点集合。
 * @param axisDirection 轴方向。
 * @returns 轴向跨度；无法计算时返回 `null`。
 */
function resolveAxisProjectedSpan(points: THREE.Vector3[], axisDirection: THREE.Vector3): number | null {
  if (points.length < 2 || axisDirection.lengthSq() <= AXIS_EPSILON) {
    return null;
  }

  const normalizedAxis = axisDirection.clone().normalize();
  let minProjection = Number.POSITIVE_INFINITY;
  let maxProjection = Number.NEGATIVE_INFINITY;
  points.forEach((point) => {
    const projection = point.dot(normalizedAxis);
    if (projection < minProjection) {
      minProjection = projection;
    }
    if (projection > maxProjection) {
      maxProjection = projection;
    }
  });

  const span = maxProjection - minProjection;
  if (!Number.isFinite(span) || span <= AXIS_EPSILON) {
    return null;
  }
  return span;
}

/**
 * 从曲面边界或网格法向中解析圆柱轴。
 *
 * @param surface 曲面对象。
 * @returns 轴信息；无法解析时返回 `null`。
 */
function resolveCylinderAxis(surface: FeatureSurface): {
  axisOrigin: THREE.Vector3;
  axisDirection: THREE.Vector3;
  axisDisplayLength?: number;
} | null {
  const circles = readCircularBoundaries(surface);
  if (circles.length >= 2) {
    let bestPair: [CircularBoundaryGeometry, CircularBoundaryGeometry] | null = null;
    let maxDistanceSq = -1;
    for (let firstIndex = 0; firstIndex < circles.length; firstIndex += 1) {
      for (let secondIndex = firstIndex + 1; secondIndex < circles.length; secondIndex += 1) {
        const distanceSq = circles[firstIndex].center.distanceToSquared(circles[secondIndex].center);
        if (distanceSq > maxDistanceSq) {
          maxDistanceSq = distanceSq;
          bestPair = [circles[firstIndex], circles[secondIndex]];
        }
      }
    }

    if (bestPair && maxDistanceSq > AXIS_EPSILON) {
      const axisDirection = bestPair[1].center.clone().sub(bestPair[0].center).normalize();
      const axisOrigin = bestPair[0].center.clone().add(bestPair[1].center).multiplyScalar(0.5);
      return {
        axisOrigin,
        axisDirection,
        axisDisplayLength: Math.sqrt(maxDistanceSq),
      };
    }
  }

  if (circles.length >= 1 && circles[0].normal.lengthSq() > AXIS_EPSILON) {
    const samples = readSurfaceWorldSamples(surface);
    return {
      axisOrigin: circles[0].center.clone(),
      axisDirection: circles[0].normal.clone().normalize(),
      axisDisplayLength: resolveAxisProjectedSpan(samples.points, circles[0].normal),
    };
  }

  const samples = readSurfaceWorldSamples(surface);
  const axisDirection = resolveCylinderAxisDirectionFromNormals(samples.normals);
  if (!axisDirection) {
    return null;
  }
  const axisOrigin = resolveAxisOriginFromProjectedCircle(samples.points, axisDirection);
  if (!axisOrigin) {
    return null;
  }
  return {
    axisOrigin,
    axisDirection,
    axisDisplayLength: resolveAxisProjectedSpan(samples.points, axisDirection),
  };
}

/**
 * 从边界圆中解析圆锥轴。
 *
 * @param surface 曲面对象。
 * @returns 轴信息；无法解析时返回 `null`。
 */
function resolveConeAxis(surface: FeatureSurface): {
  axisOrigin: THREE.Vector3;
  axisDirection: THREE.Vector3;
  axisDisplayLength?: number;
} | null {
  const circles = readCircularBoundaries(surface);
  if (circles.length >= 2) {
    let bestPair: [CircularBoundaryGeometry, CircularBoundaryGeometry] | null = null;
    let maxDistanceSq = -1;
    for (let firstIndex = 0; firstIndex < circles.length; firstIndex += 1) {
      for (let secondIndex = firstIndex + 1; secondIndex < circles.length; secondIndex += 1) {
        const distanceSq = circles[firstIndex].center.distanceToSquared(circles[secondIndex].center);
        if (distanceSq > maxDistanceSq) {
          maxDistanceSq = distanceSq;
          bestPair = [circles[firstIndex], circles[secondIndex]];
        }
      }
    }

    if (bestPair && maxDistanceSq > AXIS_EPSILON) {
      return {
        axisOrigin: bestPair[0].center.clone().add(bestPair[1].center).multiplyScalar(0.5),
        axisDirection: bestPair[1].center.clone().sub(bestPair[0].center).normalize(),
        axisDisplayLength: Math.sqrt(maxDistanceSq),
      };
    }
  }

  if (circles.length >= 1 && circles[0].normal.lengthSq() > AXIS_EPSILON) {
    const samples = readSurfaceWorldSamples(surface);
    return {
      axisOrigin: circles[0].center.clone(),
      axisDirection: circles[0].normal.clone().normalize(),
      axisDisplayLength: resolveAxisProjectedSpan(samples.points, circles[0].normal),
    };
  }

  return null;
}

/**
 * 将拓扑曲面种类映射为基准轴求解语义。
 *
 * @param surfaceKind 拓扑曲面类型。
 * @returns 支持的映射；不支持时返回 `null`。
 */
function mapSupportedSurfaceAxisKind(surfaceKind: TopologySurfaceKind): SupportedSurfaceAxisKind | null {
  if (surfaceKind === 'cylinder') {
    return {
      entityType: ReferenceEntitySemanticType.FeatureSurface,
      surfaceType: AxisSurfaceType.Cylindrical,
    };
  }
  if (surfaceKind === 'cone') {
    return {
      entityType: ReferenceEntitySemanticType.FeatureSurface,
      surfaceType: AxisSurfaceType.Conical,
    };
  }
  return null;
}

/**
 * 解析特征曲面的中心轴语义。
 *
 * 设计说明：
 * 1. 仅当曲面真实语义为圆柱面/圆锥面时，才允许进入基准轴的 CylOrConeFace 分支；
 * 2. 优先复用边界圆的几何真相，避免直接从三角面片做不稳定拟合；
 * 3. 圆柱面在缺少圆形边界时，再退回到法向反推，保证被平面裁切后的侧壁仍可建轴。
 *
 * @param surface 特征曲面对象。
 * @returns 可供基准轴控制器消费的解析结果；无法解析时返回 `null`。
 */
export function resolveFeatureSurfaceAxis(surface: FeatureSurface): FeatureSurfaceAxisResolution | null {
  const supportedKind = mapSupportedSurfaceAxisKind(surface.surfaceKind);
  if (!supportedKind) {
    return null;
  }

  const resolvedAxis = supportedKind.surfaceType === AxisSurfaceType.Cylindrical
    ? resolveCylinderAxis(surface)
    : resolveConeAxis(surface);
  if (!resolvedAxis) {
    return null;
  }

  const axisDirection = resolvedAxis.axisDirection.clone();
  if (axisDirection.lengthSq() <= AXIS_EPSILON) {
    return null;
  }

  return {
    entityType: supportedKind.entityType,
    surfaceType: supportedKind.surfaceType,
    axisOrigin: resolvedAxis.axisOrigin.clone(),
    axisDirection: axisDirection.normalize(),
    axisDisplayLength: resolvedAxis.axisDisplayLength,
    surfacePoint: surface.getWorldPoint(),
    faceOuterLoopVertices: surface.getWorldBoundaryVertices(),
  };
}
